import { buildGs1DigitalLinkUrl } from '@myplayer/gs1';
import {
  type IdentifierRecord,
  SECTOR_TEMPLATES,
  formatPlainGtinCheckSummary,
  isIdentifierRecalled,
} from '@myplayer/schema';
import { IconEdit, IconExternalLink, IconProducts, IconRecall } from '@myplayer/ui';
import { type ReactElement } from 'react';
import { useAuth } from './auth-context.js';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardIcon,
  CardRow,
  CardTitle,
  Tag,
} from './components/ui/primitives.js';
import { ProductReviewChangesCard } from './product-review-changes.js';
import { StatusBadge } from './product-status-badge.js';
import {
  type DuplicateIdentifierConflict,
  formatPlainValidationError,
  resolveIdentifierDisplayStatus,
  resolveIdentifierLifecycleState,
} from './products-firestore.js';
import {
  formatLastChangedDate,
  formatPlainLinkTypeLabel,
  resolveActiveRuleOverride,
  resolveAppliedScheduledChange,
  resolvePendingFiredScheduledChange,
  resolvePlainProductStatus,
  resolveRecordLastChangedIso,
  resolveScheduledChange,
} from './products-helpers.js';
import { formatCapabilityRestrictionNotice } from './rbac-ui.js';
import { resolveEffectiveHost } from './use-workspace-identifiers.js';

export { StatusBadge };

export interface ProductDetailPanelProps {
  readonly selectedRecord: IdentifierRecord;
  readonly effectiveResolverHost?: string | undefined;
  readonly canEdit?: boolean;
  readonly isCheckingPromotion?: boolean;
  readonly discardingDraft?: boolean;
  readonly discardDraftError?: string | null;
  readonly duplicateConflicts?: ReadonlyMap<string, DuplicateIdentifierConflict> | undefined;
  readonly onDiscardDraft?: () => void;
  readonly onStartEdit?: () => void;
}

export function ProductDetailPanel({
  selectedRecord,
  effectiveResolverHost,
  canEdit = false,
  isCheckingPromotion = false,
  discardingDraft = false,
  discardDraftError = null,
  duplicateConflicts,
  onDiscardDraft,
  onStartEdit,
}: ProductDetailPanelProps): ReactElement {
  const { session } = useAuth();
  const duplicateConflict = duplicateConflicts?.get(
    `${selectedRecord.orgId ?? ''}::${selectedRecord.workspaceId ?? ''}::${selectedRecord.identifierKey}`,
  );
  const recalled = isIdentifierRecalled(selectedRecord);
  const plainStatus = resolvePlainProductStatus(selectedRecord);
  const lifecycleState = resolveIdentifierLifecycleState(selectedRecord);
  const scheduled = resolveScheduledChange(selectedRecord);
  const pendingFired = resolvePendingFiredScheduledChange(selectedRecord);
  const appliedSchedule = resolveAppliedScheduledChange(selectedRecord);
  const activeRuleOverride = !recalled ? resolveActiveRuleOverride(selectedRecord) : null;
  const displayTitle =
    selectedRecord.draft?.title ?? selectedRecord.title ?? selectedRecord.primaryKey;
  const brand = selectedRecord.draft?.brand ?? selectedRecord.brand;
  const hasLiveDefaultUrl =
    selectedRecord.defaultUrl !== undefined && selectedRecord.defaultUrl.trim().length > 0;
  const rawDefaultUrl = hasLiveDefaultUrl
    ? selectedRecord.defaultUrl
    : selectedRecord.draft?.defaultUrl;
  const activeLinks = (
    hasLiveDefaultUrl
      ? selectedRecord.links && selectedRecord.links.length > 0
        ? selectedRecord.links
        : (selectedRecord.draft?.links ?? [])
      : (selectedRecord.draft?.links ?? selectedRecord.links ?? [])
  ).filter((l) => l.removed !== true);
  const activeCustomerUrl = recalled
    ? selectedRecord.recallOverride?.overrideUrl &&
      selectedRecord.recallOverride.overrideUrl.trim().length > 0
      ? selectedRecord.recallOverride.overrideUrl
      : 'Safety recall notice page'
    : (activeRuleOverride?.targetHref ??
      pendingFired?.targetHref ??
      (rawDefaultUrl && rawDefaultUrl.trim().length > 0 ? rawDefaultUrl : 'Not set yet'));
  const hasRuleOverrideDiff =
    !recalled &&
    activeRuleOverride !== null &&
    rawDefaultUrl !== undefined &&
    rawDefaultUrl.trim().length > 0 &&
    activeRuleOverride.targetHref !== rawDefaultUrl.trim();
  const uniqueValidationMessages =
    selectedRecord.validation?.status === 'INVALID'
      ? Array.from(
          new Set(selectedRecord.validation.errors.map((err) => formatPlainValidationError(err))),
        )
      : [];
  const matchedActiveLink = !recalled
    ? (activeLinks.find((l) => l.defaultLink && l.href === activeCustomerUrl) ??
      activeLinks.find((l) => l.href === activeCustomerUrl))
    : undefined;
  const lastChangedIso = resolveRecordLastChangedIso(selectedRecord);
  const resolvedHost = resolveEffectiveHost(effectiveResolverHost);
  const resolverUrl =
    resolvedHost.length > 0
      ? buildGs1DigitalLinkUrl({
          host: resolvedHost,
          ai: selectedRecord.ai,
          primaryKey: selectedRecord.primaryKey,
        })
      : '';
  const sectorId = selectedRecord.sector ?? 'RETAIL_CPG';
  const sectorLabel = SECTOR_TEMPLATES[sectorId]?.name ?? sectorId;
  const gtinCheckSummary = formatPlainGtinCheckSummary(selectedRecord.gs1Check);
  const gtinCheckBadgeVariant =
    gtinCheckSummary.tone === 'neutral' ? 'secondary' : gtinCheckSummary.tone;

  return (
    <Card data-testid="product-detail-panel">
      <CardHeader>
        <div className="flex min-w-0 items-center gap-3">
          <CardIcon>{recalled ? <IconRecall /> : <IconProducts />}</CardIcon>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="truncate">{displayTitle}</CardTitle>
              {brand !== undefined && brand.length > 0 && <Tag variant="outline">{brand}</Tag>}
              <StatusBadge
                status={
                  duplicateConflict ? 'invalid' : resolveIdentifierDisplayStatus(selectedRecord)
                }
                lifecycleState={lifecycleState}
                plainStatus={plainStatus}
                recalled={recalled}
                isCheckingPromotion={isCheckingPromotion}
                pendingFired={pendingFired !== null}
                mode="detail"
              />
              {duplicateConflict !== undefined && (
                <Badge data-testid="duplicate-gtin-conflict-badge" variant="destructive">
                  Duplicate GTIN conflict
                </Badge>
              )}
            </div>
            <p className="mt-0.5 font-mono text-[12px] text-fg-secondary tabular-nums">
              GTIN {selectedRecord.primaryKey}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {canEdit && onStartEdit !== undefined && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              data-testid="edit-product-button"
              onClick={onStartEdit}
            >
              <IconEdit />
              <span>Edit</span>
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!canEdit && (
          <div
            data-testid="links-editor-readonly-notice"
            className="rounded-control border border-subtle bg-surface-muted px-3 py-2 text-[12px] text-fg-secondary"
          >
            {formatCapabilityRestrictionNotice({
              capability: 'identifier:create_stage_update',
              actionSummary: 'add, edit, or remove target links in draft',
              callerRole: session?.personaRole,
            })}
          </div>
        )}

        {duplicateConflict !== undefined && (
          <div
            role="alert"
            data-testid="duplicate-gtin-conflict-banner"
            className="rounded-control border border-status-error-border bg-status-error-bg px-3.5 py-2.5 text-[12px] text-status-error"
          >
            <p className="font-semibold">Duplicate GTIN conflict:</p>
            <p className="mt-0.5 font-medium">{duplicateConflict.message}</p>
          </div>
        )}

        {uniqueValidationMessages.length > 0 && (
          <div
            role="alert"
            data-testid="draft-validation-errors"
            className="rounded-control border border-status-error-border bg-status-error-bg px-3.5 py-2.5 text-[12px] text-status-error"
          >
            <p className="font-semibold">Needs attention — please update this link:</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-4">
              {uniqueValidationMessages.map((msg, idx) => (
                <li key={idx}>
                  <span className="font-medium">{msg}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {selectedRecord.draft !== undefined && (
          <ProductReviewChangesCard
            selectedRecord={selectedRecord}
            canEdit={canEdit}
            isCheckingPromotion={isCheckingPromotion}
            discardingDraft={discardingDraft}
            discardDraftError={discardDraftError}
            {...(onDiscardDraft !== undefined ? { onDiscardDraft } : {})}
          />
        )}

        <div
          data-testid="customer-scan-preview"
          className="divide-y divide-subtle border-y border-subtle"
        >
          {resolverUrl.length > 0 && (
            <CardRow label="Resolver link">
              <a
                href={resolverUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-resolver-url"
                className="inline-flex min-w-0 items-center gap-1.5 font-mono text-[12px] font-semibold text-fg underline decoration-subtle underline-offset-2 hover:text-brand"
              >
                <IconExternalLink className="shrink-0 text-fg-secondary" />
                <span className="truncate">{resolverUrl}</span>
              </a>
            </CardRow>
          )}
          <CardRow label="Scanning goes to">
            <div className="flex min-w-0 flex-col items-end gap-1 text-right">
              {hasRuleOverrideDiff && (
                <Badge variant="warning" data-testid="active-rule-override-badge">
                  Rule override ({activeRuleOverride.ruleId})
                </Badge>
              )}
              {matchedActiveLink !== undefined && (
                <div className="flex flex-wrap items-center justify-end gap-1.5">
                  <span
                    data-testid="scanning-goes-to-link-title"
                    className="text-[12px] font-semibold text-fg"
                  >
                    {matchedActiveLink.title}
                  </span>
                  <Tag variant="outline">
                    {formatPlainLinkTypeLabel(matchedActiveLink.linkType)}
                  </Tag>
                </div>
              )}
              {/^https?:\/\//i.test(activeCustomerUrl) ? (
                <a
                  href={activeCustomerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="scanning-goes-to-url"
                  className="inline-flex min-w-0 items-center gap-1.5 font-mono text-[12px] font-semibold text-fg underline decoration-subtle underline-offset-2 hover:text-brand"
                >
                  <IconExternalLink className="shrink-0 text-fg-secondary" />
                  <span className="truncate">{activeCustomerUrl}</span>
                </a>
              ) : (
                <span
                  data-testid="scanning-goes-to-url"
                  className="inline-flex min-w-0 items-center gap-1.5 font-mono text-[12px] font-semibold text-fg"
                >
                  <IconExternalLink className="shrink-0 text-fg-secondary" />
                  <span className="truncate">{activeCustomerUrl}</span>
                </span>
              )}
            </div>
          </CardRow>
          {hasRuleOverrideDiff && rawDefaultUrl && (
            <CardRow label="Default link">
              <span
                data-testid="default-link-url"
                className="truncate font-mono text-[12px] text-fg-secondary"
              >
                {rawDefaultUrl}
              </span>
            </CardRow>
          )}
          <CardRow label="Sector">
            <span className="text-[13px] font-medium text-fg">{sectorLabel}</span>
          </CardRow>
          <CardRow label="Status">
            <div
              data-testid="view-mode-status-badge"
              className="flex flex-wrap items-center justify-end gap-1.5"
            >
              {recalled ? (
                <span className="text-[12px] font-medium text-status-error">
                  Needs attention · Recall active
                </span>
              ) : plainStatus === 'NEEDS_ATTENTION' ? (
                <span className="text-[12px] font-medium text-status-error">Needs attention</span>
              ) : pendingFired !== null ? (
                <span
                  data-testid="status-switching-note"
                  className="text-[12px] font-medium text-status-warning"
                >
                  Switching now…
                </span>
              ) : isCheckingPromotion ? (
                <span className="text-[12px] font-medium text-status-warning">Checking…</span>
              ) : lifecycleState === 'draft' || lifecycleState === 'pending' ? (
                <span className="text-[12px] font-medium text-status-warning">
                  Has unpublished changes
                </span>
              ) : (
                <span className="text-[12px] font-medium text-fg">Live now</span>
              )}
              {scheduled !== null && !recalled && (
                <span className="text-[12px] font-medium text-fg-secondary">· Scheduled</span>
              )}
              {scheduled === null && appliedSchedule !== null && !recalled && (
                <span
                  data-testid="status-applied-schedule-note"
                  className="text-[12px] font-medium text-fg-secondary"
                >
                  · Applied {appliedSchedule.formattedDate}
                </span>
              )}
            </div>
          </CardRow>
          <CardRow label="GS1 check" data-testid="product-gs1-check-row">
            <div className="flex flex-col items-end gap-1 text-right">
              <div className="flex flex-wrap items-center justify-end gap-1.5">
                <Badge data-testid="product-gs1-check-badge" variant={gtinCheckBadgeVariant}>
                  {gtinCheckSummary.badgeLabel}
                </Badge>
                {selectedRecord.gs1Check?.ownerMismatch === true && (
                  <Badge data-testid="product-gs1-check-owner-mismatch-badge" variant="warning">
                    Owner mismatch
                  </Badge>
                )}
              </div>
              <span
                data-testid="product-gs1-check-headline"
                className="text-[12px] font-medium text-fg"
              >
                {gtinCheckSummary.headline}
              </span>
              {gtinCheckSummary.detail !== undefined && (
                <span
                  data-testid="product-gs1-check-detail"
                  className="text-[11px] text-fg-secondary"
                >
                  {gtinCheckSummary.detail}
                </span>
              )}
              {gtinCheckSummary.advice !== undefined && (
                <span
                  data-testid="product-gs1-check-advice"
                  className="text-[11px] text-fg-secondary"
                >
                  {gtinCheckSummary.advice}
                </span>
              )}
              {gtinCheckSummary.ownerWarning !== undefined && (
                <span
                  data-testid="product-gs1-check-owner-warning"
                  className="text-[11px] font-medium text-status-warning"
                >
                  {gtinCheckSummary.ownerWarning}
                </span>
              )}
            </div>
          </CardRow>
          <CardRow label="Last changed">
            <span className="text-[12px] font-medium text-fg-secondary tabular-nums">
              {formatLastChangedDate(lastChangedIso)}
            </span>
          </CardRow>
        </div>
      </CardContent>
    </Card>
  );
}
